import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const STATUSES = ["new", "contacted", "scheduled", "archived"];
const STATUS_COLORS = {
  new: "text-[#C5A059] border-[#C5A059]/50",
  contacted: "text-sky-300 border-sky-300/40",
  scheduled: "text-emerald-300 border-emerald-300/40",
  archived: "text-[#71717A] border-white/15",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("kwe_admin_token", data.access_token);
      onLogin(data.access_token);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login" className="flex min-h-screen items-center justify-center bg-[#08080A] px-5">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        onSubmit={submit}
        className="w-full max-w-sm border border-white/10 bg-[#0F0F12] p-8"
      >
        <p className="text-xs font-semibold uppercase tracking-[0.32em] text-[#C5A059]">Kingdom Wealth</p>
        <h1 className="mt-3 text-2xl font-light tracking-tight text-[#F9F8F6]">Admin sign in</h1>
        <div className="mt-8 space-y-4">
          <div>
            <label htmlFor="admin-email" className="kwe-label">Email</label>
            <input id="admin-email" data-testid="admin-email-input" type="email" required className="kwe-input" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
          </div>
          <div>
            <label htmlFor="admin-password" className="kwe-label">Password</label>
            <input id="admin-password" data-testid="admin-password-input" type="password" required className="kwe-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>
          )}
          <button data-testid="admin-login-button" type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-60">
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </div>
      </motion.form>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("kwe_admin_token"));
  const [tab, setTab] = useState("consultations");
  const [consultations, setConsultations] = useState([]);
  const [optins, setOptins] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, o, s] = await Promise.all([
        api.get("/admin/consultations"),
        api.get("/admin/optins"),
        api.get("/admin/stats"),
      ]);
      setConsultations(c.data);
      setOptins(o.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("kwe_admin_token");
        setToken(null);
      } else {
        toast.error("Could not load dashboard data.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/consultations/${id}`, { status });
      setConsultations((prev) => prev.map((c) => (c._id === id ? { ...c, status } : c)));
      toast.success("Status updated.");
    } catch {
      toast.error("Could not update status.");
    }
  };

  if (!token) return <Login onLogin={setToken} />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#08080A] text-[#F9F8F6]">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 sm:px-8">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-[#C5A059]">Kingdom Wealth Enterprises</p>
            <h1 className="mt-1 text-xl font-light tracking-tight">Client Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh-button" onClick={load} aria-label="Refresh" className="border border-white/15 p-2.5 text-[#A1A1AA] transition-colors hover:border-[#C5A059]/50 hover:text-[#C5A059]">
              <RefreshCw size={15} />
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={() => { localStorage.removeItem("kwe_admin_token"); setToken(null); }}
              className="flex items-center gap-2 border border-white/15 px-4 py-2.5 text-xs font-medium uppercase tracking-[0.18em] text-[#A1A1AA] transition-colors hover:border-[#C5A059]/50 hover:text-[#C5A059]"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        {stats && (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[
              { label: "Consultation Requests", value: stats.total_consultations, id: "stat-total-consultations" },
              { label: "New This Month", value: stats.bookings_this_month, id: "stat-bookings-month" },
              { label: "Awaiting Response", value: stats.new_requests, id: "stat-new-requests" },
              { label: "Email Subscribers", value: stats.total_optins, id: "stat-total-optins" },
            ].map((s) => (
              <div key={s.id} data-testid={s.id} className="border border-white/10 bg-[#0F0F12] p-6">
                <p className="text-3xl font-light text-[#C5A059]">{s.value}</p>
                <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#A1A1AA]">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 flex gap-2">
          {["consultations", "optins"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`border px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] transition-colors ${
                tab === t ? "border-[#C5A059]/60 bg-[#C5A059]/10 text-[#C5A059]" : "border-white/10 text-[#A1A1AA] hover:text-[#F9F8F6]"
              }`}
            >
              {t === "consultations" ? `Consultations (${consultations.length})` : `Opt-ins (${optins.length})`}
            </button>
          ))}
        </div>

        {loading ? (
          <p data-testid="admin-loading" className="mt-16 text-center text-sm text-[#71717A]">Loading...</p>
        ) : tab === "consultations" ? (
          <div data-testid="consultations-table" className="mt-6 overflow-x-auto border border-white/10">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#71717A]">
                  <th className="px-5 py-4">Received</th>
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Topic</th>
                  <th className="px-5 py-4">Preferred Time</th>
                  <th className="px-5 py-4">Message</th>
                  <th className="px-5 py-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {consultations.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-12 text-center text-[#71717A]">No consultation requests yet.</td></tr>
                )}
                {consultations.map((c) => (
                  <tr key={c._id} data-testid={`consultation-row-${c._id}`} className="border-b border-white/5 align-top transition-colors hover:bg-white/[0.02]">
                    <td className="whitespace-nowrap px-5 py-4 text-[#A1A1AA]">{fmtDate(c.created_at)}</td>
                    <td className="px-5 py-4 font-medium">{c.first_name} {c.last_name}</td>
                    <td className="px-5 py-4 text-[#A1A1AA]">{c.email}</td>
                    <td className="px-5 py-4 text-[#C9C9CF]">{c.topic}</td>
                    <td className="px-5 py-4 text-[#A1A1AA]">{c.preferred_time || "-"}</td>
                    <td className="max-w-[220px] truncate px-5 py-4 text-[#71717A]" title={c.message || ""}>{c.message || "-"}</td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`status-select-${c._id}`}
                        value={c.status}
                        onChange={(e) => updateStatus(c._id, e.target.value)}
                        className={`border bg-transparent px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] ${STATUS_COLORS[c.status] || STATUS_COLORS.new}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s} className="bg-[#0F0F12] text-[#F9F8F6]">{s}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div data-testid="optins-table" className="mt-6 overflow-x-auto border border-white/10">
            <table className="w-full min-w-[560px] text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#71717A]">
                  <th className="px-5 py-4">Joined</th>
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Email</th>
                </tr>
              </thead>
              <tbody>
                {optins.length === 0 && (
                  <tr><td colSpan={3} className="px-5 py-12 text-center text-[#71717A]">No subscribers yet.</td></tr>
                )}
                {optins.map((o) => (
                  <tr key={o._id} data-testid={`optin-row-${o._id}`} className="border-b border-white/5 transition-colors hover:bg-white/[0.02]">
                    <td className="whitespace-nowrap px-5 py-4 text-[#A1A1AA]">{fmtDate(o.created_at)}</td>
                    <td className="px-5 py-4 font-medium">{o.first_name} {o.last_name}</td>
                    <td className="px-5 py-4 text-[#A1A1AA]">{o.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
